import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { connect, ConnectedProps } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import SignalR from 'Components/UI/SignalR';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import FederatedDrawer from './components/FederatedDrawer';
import SearchViewerInput from 'Components/UI/SearchViewerInput';
import LoadingDrawer from 'Components/UI/CustomLoading/LoadingDrawer';
import Models from './components/Models';
import EmptyViewer from './components/EmptyViewer';
import EmptyModels from './components/EmptyModels';
import VideoEx from 'Components/VideoEx';
import FileDestination from './Modals/FileDestination';
import CTAViewerUpgrade from './Modals/CTAViewerUpgrade';
import CTAMultipleModels from './Modals/CTAMultipleModels';
import CTAUpgradeAccessViewer from './components/CTAUpgradeAccessViewer';
import ModelsViewpoints from './components/ModelsViewpoints';
import ModelsPranchas from './components/ModelsPranchas';
import ActivitiesList from './components/ActivitiesList';
import ProgressRenderBar from 'Pages/ListagemArquivos/Modals/ProgressRenderBar';
import useFederatedViewer from './useFederatedViewer';
import useShowCreateActivityViewer from './hooks/useShowCreateActivityViewer';
import ShowCreateActivity from 'Pages/Activities/Modals/ShowCreateActivity';
import InternalViewer from 'Components/UI/InternalViewer';
import ModelsViews from './components/ModelsViews';
import toastHandler from 'Utils/toastHandler';
import { ActivitiesApi } from 'Data/ActivitiesApi';
import { ActivitiesActions } from 'Store/Activities/Activities.actions';
import { IInitialViewpoint, IViewpointBimFile } from 'Data/interfaces/Activities/IViewpointData';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import ActivitiesDemo from './components/ActivitiesDemo';
import styles from './FederadoViewer.module.scss';

const FederatedViewer: FC<Props> = ({
  isLoading,
  urns,
  userInfo,
  accessViewerTab,
  modelsTree,
  viewerListDisciplines,
  loadingDiscipline,
  loadingFiles,
  hideNodes,
  selectedNode,
  userLimitsActive,
  views,
  models,
  pranchas,
  viewpoints,
  obra,
}) => {
  const { t } = useTranslation('federatedViewer');
  const activityViewpointFeatureFlag = useFeatureFlag('activity-viewpoint-feature');
  const activityViewpointFeature = activityViewpointFeatureFlag.enabled && activityViewpointFeatureFlag.value === 'test';

  const params = useParams();
  const location = useLocation();
  const [createActivityHandler, setCreateActivityHandler] = useState<(() => void) | null>(null);
  const [viewpointRequest, setViewpointRequest] = useState<{ topicId: string; viewpointId: string } | null>(null);
  const [pendingViewpoint, setPendingViewpoint] = useState<IInitialViewpoint | null>(null);
  const [restorerReady, setRestorerReady] = useState(false);
  const viewpointRestorerRef = useRef<(viewpoint: IInitialViewpoint) => void>();

  const normalizeUrn = useCallback((rawUrn: string | undefined | null): string | null => {
    if (!rawUrn) {
      return null;
    }
    return rawUrn.startsWith('urn:') ? rawUrn : `urn:${rawUrn}`;
  }, []);

  const stripUrnPrefix = useCallback((rawUrn: string | undefined | null): string | null => {
    if (!rawUrn) {
      return null;
    }
    return rawUrn.replace(/^urn:/i, '');
  }, []);

  const {
    showActivityModal,
    selectedActivity,
    isLoading: activityLoading,
    isLoadingUpdate,
    isLoadingDelete,
    topicStatus,
    topicStages,
    topicPoints,
    topicCommunicationType,
    visibilityOptions,
    priorityOptions,
    priorityNoBgOptions,
    statusOptions,
    typeOptions,
    stageOptions,
    pointsOptions,
    communicationOptions,
    userOptions,
    disciplineOptions,
    disciplines,
    updateStatus,
    getOld,
    newActivityGuid,
    editStage,
    allowOnlyStagesLinkedToDisciplinesInTopics,
    createStageLoading,
    handleOpenActivity,
    handleCloseActivity,
    handleOpenShowActivity,
  } = useShowCreateActivityViewer({
    csId: Number(params.constructionSiteId),
    userInfo,
  });

  const {
    demoVideoViewer,
    activeDrawer,
    handleActiveList,
    drawerFederatedVisible,
    closeDrawerFederated,
    showDrawerFederated,
    handleSearch,
    activeDisciplineId,
    uploadFileViewer,
    handleMaletaFileViewer,
    handleShowCTARender,
    handleShowProgressLimitRender,
    filteredEmptyDisciplines,
    openDiscipline,
    backDisciplines,
    childrenActiveDisciplines,
    searchDiscipline,
    searchDisciplineFiles,
    loadUrn,
    handleLoadUrn,
    handleActiveUrns,
    handleOpenCTAModal,
    handleToogleNode,
    handleToogleSelectNode,
    dispatch,
    handleOpenVideoModal,
    handleCloseVideoModal,
    handleUpgradePlan,
    searchItens,
    childrenActiveUrnsNames,
    toggleView,
    handleToogleView,
    toggleViewCamera,
    handleToogleViewCamera,
    handleTooglePrancha,
    toggleHideShowNode,
    toggleSelectNode,
    togglePrancha,
    handleUploadFileViewer,
    showVideo,
    showSelectUploadPath,
    cancelUploadFileViewer,
    upfileViewer,
    selectUploadPathFromMaleta,
    showCTAViewerUpgrade,
    setShowCTAViewerUpgrade,
    showCTAMultipleModelsUpgrade,
    handleCloseCTAModal,
    indexList,
    showModalProgressLimitRender,
    handleCloseProgressLimitRender,
    handleClearAllToogles,
  } = useFederatedViewer({
    constructionSiteId: Number(params.constructionSiteId),
    isLoading,
    urns,
    userInfo,
    viewerListDisciplines,
    loadingDiscipline,
  });

  const findExtensionForUrn = useCallback(
    (urn: string): string | undefined => {
      const urnWithoutPrefix = stripUrnPrefix(urn);
      if (!urnWithoutPrefix) {
        return undefined;
      }

      for (const discipline of viewerListDisciplines) {
        const match = discipline.FilesList?.find(
          (file) => stripUrnPrefix(file.FileInfo?.BucketFile?.Urn || '') === urnWithoutPrefix,
        );
        if (match?.Extension) {
          return match.Extension;
        }
      }
      return undefined;
    },
    [stripUrnPrefix, viewerListDisciplines],
  );

  const ensureModelsLoaded = useCallback(
    (bimFiles?: IViewpointBimFile[] | null) => {
      const urnsToActivate: string[] = [];

      if (bimFiles && bimFiles.length > 0) {
        bimFiles.forEach((file) => {
          const normalized = normalizeUrn(file?.Reference || file?.Filename);
          if (normalized) {
            urnsToActivate.push(normalized);
          }
        });
      }

      if (urnsToActivate.length === 0) {
        viewerListDisciplines.forEach((discipline) => {
          discipline.FilesList?.forEach((file) => {
            const normalized = normalizeUrn(file?.FileInfo?.BucketFile?.Urn);
            if (normalized) {
              urnsToActivate.push(normalized);
            }
          });
        });
      }

      const uniqueUrns = Array.from(new Set(urnsToActivate));

      uniqueUrns.forEach((urnValue) => {
        if (!urns.includes(urnValue)) {
          const extension = findExtensionForUrn(urnValue);
          if (extension) {
            handleActiveUrns(urnValue, extension);
          }
        }
      });

      const firstPendingUrn =
        uniqueUrns.find((urnValue) => !models.some((model) => model.urn === urnValue)) ?? null;

      if (firstPendingUrn) {
        handleLoadUrn(firstPendingUrn);
      }
    },
    [findExtensionForUrn, handleActiveUrns, handleLoadUrn, models, normalizeUrn, urns, viewerListDisciplines],
  );

  // Handle create activity from drawer
  const handleCreateActivityHandlerReady = (handler: () => void) => {
    setCreateActivityHandler(() => handler);
  };

  const handleCreateActivity = () => {
    if (createActivityHandler) {
      // Use viewer's modal with viewpoint capture
      createActivityHandler();
    } else {
      // Viewer not ready yet
      toastHandler.showInfo(t('page.createActivityNotice'));
    }
  };

  const handleOpenActivityViewpoint = async (topicId: string, viewpointId: string) => {
    handleCloseActivity();

    // Update URL without page reload (allows sharing/bookmarking)
    const url = new URL(window.location.href);
    url.searchParams.set('topicId', topicId);
    url.searchParams.set('viewpointId', viewpointId);
    window.history.pushState({}, '', url.toString());

    await fetchTopicViewpoint(topicId, viewpointId);
  };

  const fetchTopicViewpoint = useCallback(async (topicId: string, viewpointId: string) => {
    setPendingViewpoint(null);

    try {
      const response = await ActivitiesApi.getTopicViewpoint(topicId, viewpointId);
      const payload = response.data?.data;

      if (!payload?.InitialViewpoint) {
        console.error('No InitialViewpoint in response');
        toastHandler.showError(t('page.viewpointNotFound'));
        return;
      }

      const viewpointData = payload.InitialViewpoint as IInitialViewpoint;

      ensureModelsLoaded(viewpointData.BimFiles || null);
      setPendingViewpoint(viewpointData); // For initial loading scenario

      // Apply viewpoint if viewer is already initialized
      if (viewpointRestorerRef.current) {
        const areRequiredModelsLoaded = () => {
          if (!viewpointData.BimFiles || viewpointData.BimFiles.length === 0) {
            return models.length > 0;
          }

          return viewpointData.BimFiles.every((bimFile) => {
            const reference = normalizeUrn(bimFile.Reference || bimFile.Filename);
            if (!reference) { return true; }

            return models.some((model) => {
              const modelUrn = model.urn;
              return modelUrn === reference ||
                stripUrnPrefix(modelUrn) === stripUrnPrefix(reference);
            });
          });
        };

        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)

        const checkAndRestore = () => {
          if (areRequiredModelsLoaded()) {
            if (viewpointRestorerRef.current) {
              viewpointRestorerRef.current(viewpointData);
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkAndRestore, 100);
          } else {
            console.warn('[FederatedViewer] Timeout waiting for models to load, restoring anyway');
            if (viewpointRestorerRef.current) {
              viewpointRestorerRef.current(viewpointData);
            }
          }
        };

        checkAndRestore();
      }
    } catch (error) {
      console.error('Error loading topic viewpoint', error);
      toastHandler.showError(t('page.viewpointLoadError'));
    }
  }, [ensureModelsLoaded, models, normalizeUrn, stripUrnPrefix]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const topicIdParam = searchParams.get('topicId');
    const viewpointIdParam = searchParams.get('viewpointId');

    if (topicIdParam && viewpointIdParam) {
      setViewpointRequest((current) => {
        if (current && current.topicId === topicIdParam && current.viewpointId === viewpointIdParam) {
          return current;
        }
        return { topicId: topicIdParam, viewpointId: viewpointIdParam };
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (!viewpointRequest) {
      return;
    }

    const { topicId, viewpointId } = viewpointRequest;
    // Reset immediately to prevent duplicate API calls on re-renders
    setViewpointRequest(null);
    fetchTopicViewpoint(topicId, viewpointId);
  }, [viewpointRequest, fetchTopicViewpoint]);

  useEffect(() => {
    if (!pendingViewpoint) {
      return;
    }

    ensureModelsLoaded(pendingViewpoint.BimFiles || null);
  }, [ensureModelsLoaded, pendingViewpoint, viewerListDisciplines]);

  // Load activities options on mount to ensure they're available for the toolbar button
  useEffect(() => {
    dispatch(ActivitiesActions.listActivities({
      csId: Number(params.constructionSiteId),
    }));
  }, [dispatch, params.constructionSiteId]);

  useEffect(() => {
    if (loadUrn) {
      return;
    }

    const nextUrn = urns.find((urnValue) => !models.some((model) => model.urn === urnValue));

    if (nextUrn) {
      handleLoadUrn(nextUrn);
    }
  }, [handleLoadUrn, loadUrn, models, urns]);

  // Viewpoint restoration now handled via initialViewpoint prop

  const isDrawerActiveModels = activeDrawer === 'models';
  const isDrawerActiveViews = activeDrawer === 'views';
  const isDrawerActiveViewPoints = activeDrawer === 'viewpoints';
  const isDrawerActivePranchas = activeDrawer === 'pranchas';
  const isDrawerActiveActivities = activeDrawer === 'activities';
  const hasUrns = urns.length > 0;
  const hasViews = views.length > 0;
  const hasViewpoints = viewpoints.length > 0;
  const hasPranchas = pranchas.length > 0;

  const isPopulatedDisciplinesChildrensLoading = ((loadingDiscipline || loadingFiles)
    || ((!isDrawerActiveModels && !isDrawerActiveActivities)
      && !hasUrns
    ));

  const searchViewerInputPlaceholder = ((!isDrawerActiveModels && !isDrawerActiveActivities)
    ? t('page.searchPlaceholderDrawer', { drawer: activeDrawer })
    : (
      activeDisciplineId
        ? t('page.searchPlaceholderModel')
        : t('page.searchPlaceholderDiscipline')
    ));

  return (
    <div className={styles['wrapper']}>
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.viewer}
        loading={loadingDiscipline || loadingFiles}
      />
      <SignalR />
      <FederatedDrawer
        activeButton={activeDrawer}
        onActiveOption={handleActiveList}
        visible={drawerFederatedVisible}
        onClose={closeDrawerFederated}
        onShow={showDrawerFederated}
      >
        <SearchViewerInput
          value={searchDiscipline || searchDisciplineFiles || searchItens}
          uploadButton={!isDrawerActiveModels || !isDrawerActiveActivities}
          onSearch={handleSearch}
          firstLoading={isPopulatedDisciplinesChildrensLoading}
          inputPlaceholder={searchViewerInputPlaceholder}
          onUploadFile={uploadFileViewer}
          onMaletaFile={handleMaletaFileViewer}
          isDebounce={false}
          limitRender={userInfo?.UserStoreItem.MaxQtyRenderedFiles || userInfo?.UserStoreItem.StorePlan?.MaxQtyRenderedFiles}
          activeRender={userLimitsActive?.QtyRenderedFiles}
          onShowCTARender={handleShowCTARender}
          onLimitBarProgress={handleShowProgressLimitRender}
        />
        {(loadingDiscipline || loadingFiles) ? (
          <LoadingDrawer multiple={6} />
        ) : (
          <>
            {isDrawerActiveModels && (
              (filteredEmptyDisciplines.length > 0) ? (
                <Models
                  activeDisciplineId={activeDisciplineId}
                  openDiscipline={openDiscipline}
                  backDisciplines={backDisciplines}
                  disciplineList={filteredEmptyDisciplines}
                  childrenDisciplinesActive={childrenActiveDisciplines}
                  searchDiscipline={searchDiscipline}
                  searchDisciplineFiles={searchDisciplineFiles}
                  loadUrn={loadUrn}
                  onLoadUrn={handleLoadUrn}
                  onActiveUrns={handleActiveUrns}
                  accessViewerTab={accessViewerTab}
                  urns={urns}
                  onCTAMultipleRender={handleOpenCTAModal}
                  modelsTree={modelsTree}
                  onToggleNode={handleToogleNode}
                  hideNodes={hideNodes}
                  onToggleSelectNode={handleToogleSelectNode}
                  selectedNode={selectedNode}
                  constructionSiteId={Number(params.constructionSiteId)}
                  api={obra.Api}
                  userInfo={userInfo}
                  dispatch={dispatch}
                />
              ) : (
                <EmptyModels
                  onPlay={() => handleOpenVideoModal()}
                  isModelActive
                />
              )
            )}
            {isDrawerActiveViews && (
              accessViewerTab ? (
                <>
                  {(hasUrns && hasViews) && (
                    <ModelsViews
                      listItems={views}
                      searchItems={searchItens}
                      childrenNames={childrenActiveUrnsNames}
                      activeDrawer={activeDrawer}
                      toggleView={toggleView}
                      onToggleView={handleToogleView}
                    />
                  )}
                  {(!hasUrns || (hasUrns && !hasViews)) && (
                    <EmptyModels
                      onPlay={() => handleActiveList('models')}
                      activeDrawer={activeDrawer}
                      hasUrns={hasUrns}
                    />
                  )}
                </>
              ) : (
                <CTAUpgradeAccessViewer
                  title={activeDrawer}
                  role={userInfo?.CurrentRoleFk}
                  onConfirm={handleUpgradePlan}
                />
              )
            )}
            {isDrawerActiveViewPoints && (
              accessViewerTab ? (
                <>
                  {(hasUrns && hasViewpoints) && (
                    <ModelsViewpoints
                      listItems={viewpoints}
                      searchItems={searchItens}
                      childrenNames={childrenActiveUrnsNames}
                      activeDrawer={activeDrawer}
                      toggleViewCamera={toggleViewCamera}
                      onToggleViewCamera={handleToogleViewCamera}
                    />
                  )}
                  {(!hasUrns || (hasUrns && !hasViewpoints)) && (
                    <EmptyModels
                      onPlay={() => handleActiveList('models')}
                      activeDrawer={activeDrawer}
                      hasUrns={hasUrns}
                    />
                  )}
                </>
              ) : (
                <CTAUpgradeAccessViewer
                  title={activeDrawer}
                  role={userInfo?.CurrentRoleFk}
                  onConfirm={handleUpgradePlan}
                />
              )
            )}
            {isDrawerActivePranchas && (
              accessViewerTab ? (
                <>
                  {(hasUrns && hasPranchas) && (
                    <ModelsPranchas
                      listItems={pranchas}
                      searchItems={searchItens}
                      childrenNames={childrenActiveUrnsNames}
                      activeDrawer={activeDrawer}
                      togglePrancha={togglePrancha}
                      onTogglePrancha={handleTooglePrancha}
                    />
                  )}
                  {(!hasUrns || (hasUrns && !hasPranchas)) && (
                    <EmptyModels
                      onPlay={() => handleActiveList('models')}
                      activeDrawer={activeDrawer}
                      hasUrns={hasUrns}
                    />
                  )}
                </>
              ) : (
                <CTAUpgradeAccessViewer
                  title={activeDrawer}
                  role={userInfo?.CurrentRoleFk}
                  onConfirm={handleUpgradePlan}
                />
              )
            )}
            {isDrawerActiveActivities && (
              activityViewpointFeature ? (
                <ActivitiesList
                  constructionSiteId={Number(params.constructionSiteId)}
                  onCreateActivity={handleCreateActivity}
                  onOpenActivity={handleOpenActivity}
                />
              ) : (
                <ActivitiesDemo />
              )
            )}
          </>
        )}
      </FederatedDrawer>
      <div className={styles['mainWrapper']}>
        <div className={drawerFederatedVisible ? styles['viewerWrapperDrawerOpen'] : styles['viewerWrapper']}>
          {!hasUrns ? (
            <EmptyViewer
              isDrawerVisible={drawerFederatedVisible}
            />
          ) : (
            <InternalViewer
              loadUrn={loadUrn}
              drawerOpen={drawerFederatedVisible}
              toggleNode={toggleHideShowNode}
              togglePrancha={togglePrancha}
              toggleSelectNode={toggleSelectNode}
              toggleViewCamera={toggleViewCamera}
              toggleView={toggleView}
              initialViewpoint={pendingViewpoint}
              visibilityOptions={visibilityOptions}
              priorityOptions={priorityOptions}
              priorityNoBgOptions={priorityNoBgOptions}
              typeOptions={typeOptions}
              stageOptions={stageOptions}
              pointsOptions={pointsOptions}
              communicationOptions={communicationOptions}
              userOptions={userOptions}
              disciplineOptions={disciplineOptions}
              disciplines={disciplines}
              stages={topicStages}
              allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
              createStageLoading={createStageLoading}
              activityViewpointFeature={activityViewpointFeature}
              onLoadUrn={handleLoadUrn}
              onActiveUrns={handleActiveUrns}
              onToggleNode={handleToogleNode}
              onToggleSelectNode={handleToogleSelectNode}
              onTogglePrancha={handleTooglePrancha}
              onToggleViewCamera={handleToogleViewCamera}
              onToggleView={handleToogleView}
              onClearAllToggles={handleClearAllToogles}
              onCreateActivityHandlerReady={handleCreateActivityHandlerReady}
              onRegisterViewpointRestorer={(handler: (viewpoint: IInitialViewpoint) => void) => {
                viewpointRestorerRef.current = handler;
                setRestorerReady(true);
              }}
            />
          )}
        </div>
      </div>
      <input
        id='uploadFileViewer'
        type='file'
        hidden
        onChange={(e) => {
          handleUploadFileViewer(e.currentTarget.files);
          e.currentTarget.value = '';
        }}
      />
      {showVideo && (
        <VideoEx
          visible={showVideo}
          url={demoVideoViewer}
          onCancel={() => handleCloseVideoModal()}
        />
      )}
      {showSelectUploadPath && (
        <FileDestination
          visible={showSelectUploadPath}
          disciplineList={filteredEmptyDisciplines}
          onCancel={() => cancelUploadFileViewer()}
          file={upfileViewer}
          currentDiscipline={activeDisciplineId}
          constructionSiteId={Number(params.constructionSiteId)}
          fromMaleta={selectUploadPathFromMaleta}
        />
      )}
      {showCTAViewerUpgrade && (
        <CTAViewerUpgrade
          visible={showCTAViewerUpgrade}
          onCancel={() => setShowCTAViewerUpgrade(false)}
          onConfirm={handleUpgradePlan}
          role={userInfo?.CurrentRoleFk}
        />
      )}
      {showCTAMultipleModelsUpgrade && (
        <CTAMultipleModels
          onConfirm={handleUpgradePlan}
          onClose={handleCloseCTAModal}
          role={userInfo?.CurrentRoleFk}
          indexList={indexList}
        />
      )}
      {showModalProgressLimitRender && (
        <ProgressRenderBar
          visible={showModalProgressLimitRender}
          maxLimits={userInfo?.UserStoreItem.MaxQtyRenderedFiles || userInfo?.UserStoreItem.StorePlan?.MaxQtyRenderedFiles}
          value={userLimitsActive?.QtyRenderedFiles || userInfo?.QtyRenderedFiles || 0}
          onCancel={handleCloseProgressLimitRender}
          onConfirmRender={uploadFileViewer}
          inFederatedViewer
        />
      )}
      {showActivityModal && (
        <ShowCreateActivity
          visible={showActivityModal}
          loadingExternalLink={false}
          loading={activityLoading}
          loadingUpdate={isLoadingUpdate}
          loadingDelete={isLoadingDelete}
          editStage={editStage}
          updateActivity={selectedActivity}
          csId={Number(params.constructionSiteId)}
          title={t('page.activityTitle')}
          topicStatus={topicStatus}
          topicStages={topicStages}
          topicPoints={topicPoints}
          topicCommunicationType={topicCommunicationType}
          visibilityOptions={visibilityOptions}
          priorityOptions={priorityOptions}
          priorityNoBgOptions={priorityNoBgOptions}
          statusOptions={statusOptions}
          typeOptions={typeOptions}
          stageOptions={stageOptions}
          pointsOptions={pointsOptions}
          communicationOptions={communicationOptions}
          userOptions={userOptions}
          disciplineOptions={disciplineOptions}
          disciplines={disciplines}
          allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
          createStageLoading={createStageLoading}
          updateStatus={updateStatus}
          getOld={getOld}
          newActivityGuid={newActivityGuid}
          userInfo={userInfo}
          onOpenShowActivity={handleOpenShowActivity}
          onOpenViewpoint={handleOpenActivityViewpoint}
          onCancel={handleCloseActivity}
        />
      )}
    </div>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.viewer,
  ...state.federatedViewer,
  userInfo: state.auth.userInfo,
  planFree: state.auth.planFree,
  userLimitsActive: state.auth.userLimitsActive,
  accessViewerTab: state.auth.userInfo?.UserStoreItem.StorePlan?.AccessViewerTab,
  isLoading: state.federatedViewer.isLoading,
  obra: state.obra.Obra,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(FederatedViewer);
